import { measureRequest } from './analytics';
import type {
  AccountProfile,
  NoteIconInput,
  PersonAccess,
  DirectoryDepartment,
  Invitation,
  InvitationPreview,
  AdminUser,
  AgentToken,
  AuditEntry,
  AuthConfig,
  Department,
  DeptListEntry,
  DeptStats,
  IndustryPack,
  Me,
  MfaStatus,
  OrgRole,
  NoteDetail,
  NoteSummary,
  NoteVersion,
  NoteLibraryResult,
  Role,
  SearchHit,
  TreeNode,
  VaultGraph,
  AccessMode,
  AccessPreview,
  AccessQueueEntry,
  AccessRequest,
  AiAccess,
  EffectiveAccess,
  SensitivityCategoryPolicy,
  StoredMapStyle,
} from './types';
import type { AuthorizationPreview, ComposioDisconnect, ComposioState, DriveImportConsent, DriveImportResult, DriveSourceListing, ManagedConnection, IntegrationsState, WorkflowConnection, WorkflowProvider } from './integrations';
import { PLAN_LIMIT_CODES, type PlanUsage } from './plan-format';
import { describeValidation, encodeNotePath, type ReindexStats } from './lib';
import type { SopPayload, SopPreview } from './sop';

export interface BackupRun {
  id: string; requestedAt: string; startedAt: string | null; finishedAt: string | null;
  state: 'queued' | 'running' | 'completed' | 'failed'; snapshotId: string | null;
  bytes: number | null; error: string | null;
}

export interface OnboardingState {
  profile: { name: string; kind: 'personal' | 'business' | null; icon: string | null; step: 'identity' | 'structure' | 'invite' | 'complete'; completedAt: string | null };
  emailConfigured: boolean;
  limits?: { seats: number; departments: number | null; storageBytes: number; departmentsUsed: number; accountUrl: string | null } | null;
}
export interface BackupStatus {
  workspaceId: string; configured: boolean; intervalHours: number | null;
  health: 'healthy' | 'failed' | 'overdue' | 'unconfigured'; destination: 'local' | 'remote' | null;
  lastSuccess: BackupRun | null; recent: BackupRun[];
  /** When the owner may next ask for a backup (one an hour); null or absent when they may now. */
  nextOwnerRequestAt?: string | null;
}

/**
 * Thin typed wrapper over fetch. Every mutating call carries the
 * `X-Obelisk-Client` header the server's CSRF guard requires.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues?: { path: string; message: string }[],
    /**
     * Set on classified Google Drive failures. The bucket says who owns the
     * problem -- the customer's IT, Google, or us -- and the error ID is only
     * ever sent for the buckets we own. See `drive-errors.ts`.
     */
    readonly bucket?: string,
    readonly errorId?: string,
    /** When an allowance that ran out is available again. */
    readonly retryAt?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, url: string, body?: unknown, timeoutMs?: number, planNotice = true): Promise<T> {
  const record = measureRequest(method, url);
  const res = await fetch(url, {
    method,
    ...(timeoutMs ? { signal: AbortSignal.timeout(timeoutMs) } : {}),
    credentials: 'same-origin',
    headers: {
      'X-Obelisk-Client': 'web',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }).catch(error => { record(0); throw error; });

  if (res.status === 204) { record(res.status); if (method !== 'GET') window.dispatchEvent(new Event('obelisk:usage-changed')); return undefined as T; }

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      record(502);
      throw new ApiError(res.status, 'bad_response', `${method} ${url} returned non-JSON (${res.status})`);
    }
  }

  record(res.status);
  if (!res.ok) {
    const err = (parsed as { error?: { code?: string; message?: string; issues?: { path: string; message: string }[]; bucket?: string; errorId?: string; retryAt?: string } })
      ?.error;
    if (err?.code && PLAN_LIMIT_CODES.has(err.code)) {
      window.dispatchEvent(new Event('obelisk:usage-changed'));
      if (planNotice) window.dispatchEvent(new CustomEvent('obelisk:plan-limit', { detail: err.message ?? 'Your workspace has reached a plan allowance.' }));
    }
    throw new ApiError(
      res.status,
      err?.code ?? 'unknown',
      describeValidation(err?.message ?? `${method} ${url} failed with ${res.status}`, err?.issues),
      err?.issues,
      err?.bucket,
      err?.errorId,
      typeof err?.retryAt === 'string' ? err.retryAt : undefined,
    );
  }
  if (method !== 'GET') window.dispatchEvent(new Event('obelisk:usage-changed'));
  return parsed as T;
}

/** Re-exported: callers take the note path encoder from the API client. */
export { encodeNotePath };

export const api = {
  planUsage: () => request<PlanUsage>('GET', '/api/plan'),
  onboarding: () => request<OnboardingState>('GET', '/api/admin/onboarding'),
  saveWorkspaceLogo: (icon: string | null) => request<OnboardingState>('PATCH', '/api/admin/workspace/logo', { icon }),
  saveWorkspaceProfile: (kind: 'personal' | 'business', name: string, icon?: string | null) => request<OnboardingState>('PUT', '/api/admin/onboarding', { kind, name, ...(icon !== undefined ? { icon } : {}) }),
  advanceOnboarding: (complete: boolean) => request<OnboardingState>('POST', '/api/admin/onboarding/advance', { complete }),
  supportAccess: () => request<import('./components/SupportAccess').SupportAccessState>('GET', '/api/admin/support'),
  approveSupportAccess: (id: string, body: { scopes: { dept: string; clearance: string }[]; hours: number }) => request('POST', `/api/admin/support/${encodeURIComponent(id)}/approve`, body),
  changeSupportAccess: (id: string, action: 'deny' | 'revoke') => request('POST', `/api/admin/support/${encodeURIComponent(id)}/${action}`, {}),
  backups: () => request<BackupStatus>('GET', '/api/admin/backups'),
  requestBackup: () => request<{ id: string }>('POST', '/api/admin/backups', {}),
  composio: () => request<ComposioState>('GET', '/api/integrations/composio'),
  configureComposio: (key: string) => request<{ ok: true; appCount: number }>('POST', '/api/integrations/composio/settings', { key }),
  connectComposio: (app: string, dept: string, planNotice = true) => request<{ id: string; redirect: string }>('POST', '/api/integrations/composio/connect', { app, dept }, undefined, planNotice),
  /** A provider session from a workspace's own project, or a ticket from the account portal. */
  completeComposio: (proof: { session: string } | { broker: string }) => request<ManagedConnection>('POST', '/api/integrations/composio/complete', proof),
  removeComposioKey: () => request<{ ok: true }>('DELETE', '/api/integrations/composio/settings'),
  checkComposio: (id: string) => request<ManagedConnection>('POST', `/api/integrations/composio/${encodeURIComponent(id)}/check`, {}),
  disconnectComposio: (id: string) => request<ComposioDisconnect>('DELETE', `/api/integrations/composio/${encodeURIComponent(id)}`),
  driveSources: (id: string, folderId?: string) => request<DriveSourceListing>('GET',
    `/api/integrations/drive/${encodeURIComponent(id)}/sources${folderId ? `?folderId=${encodeURIComponent(folderId)}` : ''}`),
  // `consent` is not optional on purpose: the server refuses an import that
  // nobody confirmed a department and classification for.
  importDriveFiles: (id: string, fileIds: string[], consent: DriveImportConsent) =>
    request<DriveImportResult>('POST', `/api/integrations/drive/${encodeURIComponent(id)}/import`, { fileIds, consent }),
  integrations: () => request<IntegrationsState>('GET', '/api/integrations'),
  integrationAuthorization: (id: string) => request<AuthorizationPreview>('GET', `/api/integrations/authorize/${encodeURIComponent(id)}`),
  authorizeIntegration: (id: string, depts: string[], write: boolean, approve: boolean, unpublished = false) =>
    request<{ redirect: string }>('POST', `/api/integrations/authorize/${encodeURIComponent(id)}`, { depts, write, approve, unpublished }),
  disconnectAssistant: (id: string) => request<{ ok: true }>('DELETE', `/api/integrations/assistants/${id}`),
  createIntegration: (input: { provider: WorkflowProvider; name: string; dept: string; classification: string }) =>
    request<{ connection: WorkflowConnection; key: string; endpoint: string }>('POST', '/api/integrations', input),
  disconnectIntegration: (id: string) => request<{ ok: true }>('DELETE', `/api/integrations/${id}`),
  rotateIntegrationKey: (id: string) => request<{ key: string; endpoint: string }>('POST', `/api/integrations/${id}/rotate`, {}),
  reviewComparison: (dept: string, path: string) => request<{ proposal: NoteDetail; current: NoteDetail | null; baseRaw: string | null; stale: boolean }>('GET', `/api/d/${dept}/review-detail/${encodeNotePath(path)}`),
  reviewAction: (dept: string, path: string, input: { action: 'approve' | 'dismiss' | 'reopen' | 'rebase'; expectedChecksum: string; targetChecksum?: string; body?: string }) => request<{ path: string }>('POST', `/api/d/${dept}/review-action/${encodeNotePath(path)}`, input),
  saveOfflinePolicy: (input: { enabled: boolean; blockedDepartments: string[] }) => request<Me['offline']>('PUT', '/api/admin/offline-policy', input),
  // auth
  // A hosted workspace machine cold-starts after a restart (a deployment or
  // configuration update; before workspaces stopped autostopping, after
  // idling), which costs several seconds before anything answers. Eight was
  // close enough to that to abort real sign-ins; the session check is retried
  // and never shows a sign-in form on a timeout, so a longer wait is cheap.
  me: () => request<Me>('GET', '/api/auth/me', undefined, 15000),
  login: (email: string, password: string) =>
    request<{ ok: boolean; mfaRequired?: boolean; challenge?: string }>('POST', '/api/auth/login', {
      email,
      password,
    }),
  loginMfa: (challenge: string, code: string) =>
    request<{ ok: true }>('POST', '/api/auth/login/mfa', { challenge, code }),
  profile: () => request<AccountProfile>('GET', '/api/auth/profile'),
  updateProfile: (name: string) => request<AccountProfile>('PATCH', '/api/auth/profile', { name }),
  mfa: () => request<MfaStatus>('GET', '/api/auth/mfa'),
  mfaEnroll: () => request<{ secret: string; uri: string }>('POST', '/api/auth/mfa/enroll', {}),
  mfaConfirm: (code: string) => request<{ recoveryCodes: string[] }>('POST', '/api/auth/mfa/confirm', { code }),
  mfaDisable: (code: string) => request<{ ok: true }>('POST', '/api/auth/mfa/disable', { code }),
  resetUserMfa: (id: string) => request<{ ok: true }>('DELETE', `/api/admin/users/${id}/mfa`),
  logout: () => request<{ ok: true }>('POST', '/api/auth/logout'),

  // departments
  departments: () => request<{ departments: DeptListEntry[] }>('GET', '/api/depts'),
  department: (slug: string) =>
    request<{
      department: Department;
      scope: { role: Role; clearance: string; permissions: string[]; viaGrant: boolean };
      stats: DeptStats;
      grants: { to_dept: string; mode: string; folder: string | null }[];
      members: { userId: string; email: string; name: string; role: Role; clearance: string | null }[];
    }>('GET', `/api/depts/${slug}`),
  createDepartment: (input: {
    name: string;
    slug?: string;
    description?: string;
    industry: string;
    extraFolders?: { path: string; description: string }[];
    omitFolders?: string[];
    defaultClassification?: string;
  }, planNotice = true) => request<{ department: Department }>('POST', '/api/depts', input, undefined, planNotice),
  updateDepartment: (slug: string, patch: Record<string, unknown>) =>
    request<{ department: Department }>('PATCH', `/api/depts/${slug}`, patch),
  addFolder: (slug: string, path: string, description: string) =>
    request<{ department: Department }>('POST', `/api/depts/${slug}/folders`, { path, description }),
  updateFolder: (slug: string, path: string, description: string) =>
    request<{ department: Department }>('PATCH', `/api/depts/${slug}/folders`, { path, description }),
  renameFolder: (slug: string, from: string, to: string) =>
    request<{ department: Department; moved: number; rewritten: number }>('POST', `/api/depts/${slug}/folders/rename`, { from, to }),
  removeFolder: (slug: string, path: string) =>
    request<{ department: Department }>('POST', `/api/depts/${slug}/folders/remove`, { path }),
  adoptPackTypes: (slug: string, pack: string) =>
    request<{ department: Department; added: string[] }>('POST', `/api/depts/${slug}/note-types`, { pack }),
  archiveDepartment: (slug: string) => request<{ department: Department }>('POST', `/api/depts/${slug}/archive`, {}),
  restoreDepartment: (slug: string) => request<{ department: Department }>('POST', `/api/depts/${slug}/restore`, {}),
  purgeDepartment: (slug: string, confirm: string) =>
    request<{ ok: true; filesDeleted: number }>('DELETE', `/api/depts/${slug}`, { confirm }),
  grantRead: (slug: string, toDept: string) =>
    request<{ grants: { to_dept: string; mode: string; folder: string | null }[] }>(
      'POST',
      `/api/depts/${slug}/grants`,
      { toDept },
    ),
  revokeRead: (slug: string, toDept: string) =>
    request<{ grants: { to_dept: string; mode: string; folder: string | null }[] }>(
      'DELETE',
      `/api/depts/${slug}/grants/${toDept}`,
    ),
  setMember: (slug: string, userId: string, role: Role, clearance: string | null) =>
    request<{ members: unknown[] }>('PUT', `/api/depts/${slug}/members/${userId}`, { role, clearance }),
  removeMember: (slug: string, userId: string) =>
    request<{ members: unknown[] }>('DELETE', `/api/depts/${slug}/members/${userId}`),

  // notes
  library: (dept: string, params: Record<string, string | number | undefined>) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value != null && value !== '') query.set(key, String(value));
}
    return request<NoteLibraryResult>('GET', `/api/d/${dept}/library?${query}`);
  },
  tree: (dept: string) => request<{ tree: TreeNode[] }>('GET', `/api/d/${dept}/tree`),
  notes: (dept: string, params: Record<string, string | number | undefined> = {}) => {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v != null && v !== '') query.set(k, String(v));
    const qs = query.toString();
    return request<{ notes: NoteSummary[] }>('GET', `/api/d/${dept}/notes${qs ? `?${qs}` : ''}`);
  },
  note: (dept: string, path: string, timeoutMs?: number) =>
    request<{ note: NoteDetail }>('GET', `/api/d/${dept}/notes/${encodeNotePath(path)}`, undefined, timeoutMs),
  previewSop: (dept: string, input: SopPayload) => request<SopPreview>('POST', `/api/d/${dept}/sops/preview`, input),
  createSop: (dept: string, input: SopPayload) =>
    request<{ path: string; checksum: string; created: boolean; folder: string; reviewBy: string }>('POST', `/api/d/${dept}/sops`, input),
  createNote: (
    dept: string,
    input: { title: string; folder?: string; type?: string; body?: string; classification?: string; tags?: string[]; icon?: NoteIconInput },
    planNotice = true,
  ) => request<{ path: string; checksum: string; created: boolean }>('POST', `/api/d/${dept}/notes`, input, undefined, planNotice),
  addSkillReference: (dept: string, path: string, input: { image: string; name?: string; description?: string; role?: string; sourceUrl?: string; expectedChecksum: string }) =>
    request<{ path: string; checksum: string; references: import('./skill-references').SkillReferenceManifest }>('POST', `/api/d/${dept}/skill-references/${encodeNotePath(path)}`, input, 60_000),
  removeSkillReference: (dept: string, path: string, input: { file: string; expectedChecksum: string }) =>
    request<{ path: string; checksum: string; removed: string; references: import('./skill-references').SkillReferenceManifest }>('DELETE', `/api/d/${dept}/skill-references/${encodeNotePath(path)}`, input),
  saveNote: (
    dept: string,
    path: string,
    input: { raw?: string; body?: string; frontmatter?: Record<string, unknown>; expectedChecksum: string; icon?: NoteIconInput; mutationId?: string; offline?: boolean },
    timeoutMs?: number,
  ) => request<{ path: string; checksum: string }>('PUT', `/api/d/${dept}/notes/${encodeNotePath(path)}`, input, timeoutMs),
  deleteNote: (dept: string, path: string, expectedChecksum: string) =>
    request<{ path: string }>('DELETE', `/api/d/${dept}/notes/${encodeNotePath(path)}`, { expectedChecksum }),
  moveNote: (dept: string, from: string, to: string, expectedChecksum: string) =>
    request<{ from: string; to: string; rewritten: number }>('POST', `/api/d/${dept}/move`, { from, to, expectedChecksum }),
  appendNote: (dept: string, path: string, content: string, section?: string) =>
    request<{ path: string }>('POST', `/api/d/${dept}/append/${encodeNotePath(path)}`, {
      content,
      ...(section ? { section, createSection: true } : {}),
    }),
  preview: (dept: string, markdown: string, path?: string) =>
    request<{ html: string }>('POST', `/api/d/${dept}/preview`, { markdown, ...(path ? { path } : {}) }),
  suggest: (dept: string, q: string) =>
    request<{ suggestions: { path: string; title: string; link: string }[] }>(
      'GET',
      `/api/d/${dept}/suggest?q=${encodeURIComponent(q)}`,
    ),
  tags: (dept: string) => request<{ tags: { tag: string; count: number }[] }>('GET', `/api/d/${dept}/tags`),
  graph: (dept: string) => request<{ graph: VaultGraph }>('GET', `/api/d/${dept}/graph`),
  mapStyle: (dept: string) =>
    request<{ style: StoredMapStyle; canEdit: boolean }>('GET', `/api/d/${dept}/map-style`),
  saveMapStyle: (dept: string, style: { grouping: string; categories: Record<string, unknown> }) =>
    request<{ style: StoredMapStyle; canEdit: boolean }>('PUT', `/api/d/${dept}/map-style`, style),
  resetMapStyle: (dept: string) =>
    request<{ style: StoredMapStyle; canEdit: boolean }>('DELETE', `/api/d/${dept}/map-style`),
  versions: (dept: string, path: string) =>
    request<{ versions: NoteVersion[] }>('GET', `/api/d/${dept}/versions/${encodeNotePath(path)}`),
  version: (dept: string, path: string, id: number) =>
    request<{ version: { content: string; createdAt: string; author: string } }>(
      'GET',
      `/api/d/${dept}/version/${id}/${encodeNotePath(path)}`,
    ),
  restoreVersion: (dept: string, path: string, id: number, expectedChecksum: string | null) =>
    request<{ path: string }>('POST', `/api/d/${dept}/restore/${id}/${encodeNotePath(path)}`, { expectedChecksum }),

  // search
  search: (q: string, params: { dept?: string[]; tag?: string; limit?: number } = {}) => {
    const query = new URLSearchParams({ q });
    for (const dept of params.dept ?? []) query.append('dept', dept);
    if (params.tag) query.set('tag', params.tag);
    if (params.limit) query.set('limit', String(params.limit));
    return request<{ hits: SearchHit[]; query: string }>('GET', `/api/search?${query.toString()}`);
  },

  // industries
  industries: () =>
    request<{ industries: IndustryPack[]; errors: { file: string; error: string }[]; customDir: string }>(
      'GET',
      '/api/industries',
    ),

  // admin
  users: () => request<{ users: AdminUser[] }>('GET', '/api/admin/users'),
  directory: () => request<{ departments: DirectoryDepartment[]; emailConfigured: boolean }>('GET', '/api/admin/directory'),
  invitations: () => request<{ invitations: Invitation[] }>('GET', '/api/admin/invitations'),
  invite: (input: PersonAccess & { email: string }, planNotice = true) => request<{ id: string }>('POST', '/api/admin/invitations', input, undefined, planNotice),
  resendInvitation: (id: string) => request<{ id: string }>('POST', `/api/admin/invitations/${id}/resend`, {}),
  revokeInvitation: (id: string) => request<{ ok: true }>('DELETE', `/api/admin/invitations/${id}`),
  previewInvitation: (token: string) => request<InvitationPreview>('POST', '/api/auth/invitation/preview', { token }),
  setAnalyticsConsent: (consent: 'allowed' | 'declined') =>
    request<{ ok: true }>('PUT', '/api/auth/analytics-consent', { consent }),
  authOptions: () => request<{ recoveryAvailable: boolean; accountUrl: string | null; google: { clientId: string; loginUri: string } | null; accountSignIn: string | null }>('GET', '/api/auth/options'),
  prepareGoogleSignIn: (returnPath: string, invitation?: string) =>
    request<{ nonce: string }>('POST', '/api/auth/google/prepare', { returnPath, ...(invitation ? { invitation } : {}) }),
  requestPasswordReset: (email: string) => request<{ ok: true }>('POST', '/api/auth/recovery/request', { email }),
  completePasswordReset: (token: string, password: string) => request<{ ok: true }>('POST', '/api/auth/recovery/complete', { token, password }),
  acceptInvitation: (token: string, name: string, password: string) => request<{ ok: true }>('POST', '/api/auth/invitation/accept', { token, name, password }),
  updateUser: (id: string, patch: Record<string, unknown>) =>
    request<{ ok: true }>('PATCH', `/api/admin/users/${id}`, patch),
  setOrgRoles: (id: string, roles: OrgRole[]) =>
    request<{ ok: true; roles: OrgRole[] }>('PATCH', `/api/admin/users/${id}/org-roles`, { roles }),
  authConfig: () => request<AuthConfig>('GET', '/api/auth/config'),
  tokens: () => request<{ tokens: AgentToken[] }>('GET', '/api/admin/tokens'),
  createToken: (input: {
    name: string;
    scope?: 'org' | 'depts';
    owner?: 'org' | 'user';
    depts: string[];
    permissions: string[];
    clearance: string;
    writeFolders: string[];
    expiresInDays?: number;
  }) => request<{ id: string; token: string; prefix: string }>('POST', '/api/admin/tokens', input),
  revokeToken: (id: string) => request<{ ok: true }>('DELETE', `/api/admin/tokens/${id}`),
  audit: (params: { dept?: string; action?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v != null && v !== '') query.set(k, String(v));
    const qs = query.toString();
    return request<{ entries: AuditEntry[] }>('GET', `/api/admin/audit${qs ? `?${qs}` : ''}`);
  },
  reindex: (dept?: string) =>
    request<{ stats: Record<string, ReindexStats> }>(
      'POST',
      `/api/admin/reindex${dept ? `?dept=${dept}` : ''}`,
      {},
    ),
  // ------------------------------------------------------------- privacy
  noteAccess: (dept: string, path: string) =>
    request<AccessPreview>('GET', `/api/access/${dept}/note/${encodeNotePath(path)}`),
  setNoteAccessMode: (dept: string, path: string, mode: AccessMode, ownerId?: string | null) =>
    request<EffectiveAccess>('PUT', `/api/access/${dept}/note/${encodeNotePath(path)}/mode`, {
      mode,
      ...(ownerId !== undefined ? { ownerId } : {}),
    }),
  setNoteAiAccess: (dept: string, path: string, aiAccess: AiAccess) =>
    request<EffectiveAccess>('PUT', `/api/access/${dept}/note/${encodeNotePath(path)}/ai`, { aiAccess }),
  addNoteAccessGrant: (
    dept: string,
    path: string,
    grant: { principalKind: 'user' | 'group'; principalId: string; capability: 'read' | 'edit' },
  ) => request<EffectiveAccess>('POST', `/api/access/${dept}/note/${encodeNotePath(path)}/grants`, grant),
  removeNoteAccessGrant: (dept: string, path: string, kind: 'user' | 'group', principalId: string) =>
    request<EffectiveAccess>(
      'DELETE',
      `/api/access/${dept}/note/${encodeNotePath(path)}/grants/${kind}/${encodeURIComponent(principalId)}`,
    ),
  quarantineNote: (dept: string, path: string, reason: string) =>
    request<EffectiveAccess>('POST', `/api/access/${dept}/note/${encodeNotePath(path)}/quarantine`, { reason }),
  releaseNote: (dept: string, path: string, note?: string) =>
    request<EffectiveAccess>('POST', `/api/access/${dept}/note/${encodeNotePath(path)}/release`, note ? { note } : {}),
  removeStoredNote: (dept: string, path: string) =>
    request<{ removed: string[] }>('DELETE', `/api/access/${dept}/note/${encodeNotePath(path)}/stored-content`),
  accessQueue: (dept: string) => request<{ notes: AccessQueueEntry[] }>('GET', `/api/access/${dept}/queue`),
  accessRequests: (dept: string) => request<{ requests: AccessRequest[] }>('GET', `/api/access/${dept}/requests`),
  decideAccessRequest: (dept: string, id: string, decision: 'approved' | 'denied', note?: string) =>
    request<{ state: string }>('POST', `/api/access/${dept}/requests/${encodeURIComponent(id)}/decide`, {
      decision,
      ...(note ? { note } : {}),
    }),
  sensitivityPolicy: () =>
    request<{ categories: SensitivityCategoryPolicy[] }>('GET', '/api/admin/sensitivity-policy'),
  updateSensitivityPolicy: (category: string, patch: Partial<SensitivityCategoryPolicy>) =>
    request<SensitivityCategoryPolicy>('PUT', `/api/admin/sensitivity-policy/${encodeURIComponent(category)}`, patch),
  system: () =>
    request<{
      counts: Record<string, number>;
      storage: { kind: string; vaultDir: string | null };
      industries: { count: number; errors: { file: string; error: string }[] };
      version: string;
    }>('GET', '/api/admin/system'),
};
